/**
 * Fonctions utilitaires pour la configuration des recettes
 */

import type { InventoryItem, RecipeIngredient } from "../inventaire/inventaireTypes";
import type { RecipeCostAnalysis, ConfigurationStats } from "./configurationTypes";

/**
 * Calcule le coût total et la marge d'une recette
 */
export function calculateRecipeCost(
  composition: RecipeIngredient[],
  allItems: InventoryItem[]
): RecipeCostAnalysis {
  let coutTotal = 0;
  const ingredientsManquants: string[] = [];

  composition.forEach((comp) => {
    const ingredient = allItems.find((item) => item.id === comp.itemId);
    if (!ingredient || !ingredient.prixCoutant) {
      ingredientsManquants.push(comp.itemId);
      return;
    }
    
    // Calcul précis basé sur le format de l'ingrédient
    if (ingredient.formatQuantite && ingredient.formatUnite) {
      // Coût par unité de mesure = prixCoutant / formatQuantite
      // Exemple: 28$ pour 750ml = 0.037$/ml
      const coutParUnite = ingredient.prixCoutant / ingredient.formatQuantite;
      
      // Coût de l'ingrédient dans la recette = coutParUnite * quantité
      // Exemple: 0.037$/ml * 60ml = 2.24$
      const coutIngredient = coutParUnite * comp.quantite;
      coutTotal += coutIngredient;
    } else {
      // Fallback: si pas de format défini, utiliser le prix coûtant comme prix unitaire
      coutTotal += ingredient.prixCoutant * comp.quantite;
    }
  });

  return {
    coutTotal,
    margePercent: 0,
    margeDollars: 0,
    prixVente: 0,
    ingredientsManquants,
  };
}

/**
 * Calcule le coût et la marge complète pour une recette avec prix de vente
 */
export function analyzeRecipe(
  recipe: InventoryItem,
  allItems: InventoryItem[]
): RecipeCostAnalysis {
  const composition = recipe.composition || [];
  const costAnalysis = calculateRecipeCost(composition, allItems);
  
  const prixVente = recipe.prixVente || 0;
  const margeDollars = prixVente - costAnalysis.coutTotal;
  const margePercent = costAnalysis.coutTotal > 0 
    ? (margeDollars / costAnalysis.coutTotal) * 100 
    : 0;

  return {
    ...costAnalysis,
    prixVente,
    margeDollars,
    margePercent,
  };
}

/**
 * Filtre les items pour obtenir uniquement les recettes
 * (items du POS qui doivent être mappés vers des ingrédients)
 */
export function getRecipeItems(items: InventoryItem[]): InventoryItem[] {
  return items.filter(
    (item) => 
      item.source === "pos" && 
      item.typeItem === "recette"
  );
}

/**
 * Filtre les items pour obtenir les ingrédients disponibles
 */
export function getAvailableIngredients(items: InventoryItem[]): InventoryItem[] {
  return items.filter(
    (item) => 
      item.typeItem === "ingredient" || 
      item.typeItem === "produit_simple" ||
      item.source === "manuel"
  );
}

/**
 * Vérifie si une recette est configurée
 */
export function isRecipeConfigured(recipe: InventoryItem): boolean {
  return recipe.composition !== undefined && recipe.composition.length > 0;
}

/**
 * Calcule les statistiques globales de configuration
 */
export function calculateConfigurationStats(
  items: InventoryItem[]
): ConfigurationStats {
  const recettes = getRecipeItems(items);
  const recettesConfigurees = recettes.filter(isRecipeConfigured);
  const recettesNonConfigurees = recettes.filter((r) => !isRecipeConfigured(r));

  // Ingrédients uniques utilisés
  const ingredientsUniques = new Set<string>();
  recettesConfigurees.forEach((recette) => {
    recette.composition?.forEach((comp) => {
      ingredientsUniques.add(comp.itemId);
    });
  });

  // Calculs de coûts et marges moyennes
  let totalCout = 0;
  let totalMarge = 0;
  let countWithCost = 0;

  recettesConfigurees.forEach((recette) => {
    const analysis = analyzeRecipe(recette, items);
    if (analysis.coutTotal > 0) {
      totalCout += analysis.coutTotal;
      totalMarge += analysis.margePercent;
      countWithCost++;
    }
  });

  return {
    totalRecettes: recettes.length,
    recettesNonConfigurees: recettesNonConfigurees.length,
    recettesConfigurees: recettesConfigurees.length,
    ingredientsUniques: ingredientsUniques.size,
    coutMoyen: countWithCost > 0 ? totalCout / countWithCost : 0,
    margeMoyenne: countWithCost > 0 ? totalMarge / countWithCost : 0,
  };
}

/**
 * Formatte un nombre en fraction commune (1/4, 1/2, 3/4, etc.)
 */
export function formatFraction(value: number): string {
  const commonFractions = [
    { value: 0.25, label: "1/4" },
    { value: 0.33, label: "1/3" },
    { value: 0.5, label: "1/2" },
    { value: 0.66, label: "2/3" },
    { value: 0.75, label: "3/4" },
  ];

  const fraction = commonFractions.find(
    (f) => Math.abs(f.value - value) < 0.02
  );
  
  if (fraction) return fraction.label;
  if (Number.isInteger(value)) return value.toString();
  return value.toFixed(2);
}